import React from 'react';
import _, { forEach } from 'lodash';
import { useMutation, useQuery } from 'react-fetching-library';
import { Modal, Card, CardContent, CardHeader, IconButton, Stack, Alert, Button, Portal, Typography, FormControlLabel, Checkbox, Select, MenuItem } from '@mui/material';
import { SmallFormTextField } from '../../../../components/small-fields/SmallFormTextField';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { useSelector } from 'react-redux';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)'
};


export default function DocumentForm({ open, handleClose, setDocuments, document, submitDocumentAction, getDocumentsAction, setCount }) {
  const refInputFile = React.useRef(null);
  const { season } = useSelector(state => state.app);
  const [functionTypes, setFunctionTypes] = React.useState([]);
  const [fileName, setFileName] = React.useState('');
  const [errorMessage, setErrorMessage] = React.useState('');
  const [choices, setChoices] = React.useState([]);
  const [periodChoices, setPeriodChoices] = React.useState([]);
  const [selectAllFunctions, setSelectAllFunctions] = React.useState(false);
  const [selectAllPeriods, setSelectAllPeriods] = React.useState(false);
  const { loading, mutate } = useMutation(submitDocumentAction);
  const optionsRequest = useQuery(getDocumentsAction('OPTIONS'));
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [periods, setPeriods] = React.useState([]);
  const [periodsNames, setPeriodsNames] = React.useState([]);

  React.useEffect(() => {
    if (!optionsRequest.loading && !optionsRequest.error) {
      setChoices(optionsRequest.payload.actions.GET.function_type.choices);
      setPeriodChoices(optionsRequest.payload.actions.GET.periods.choices);
    }
  }, [optionsRequest.loading]);

  React.useEffect(() => {
    if (document) {
      setName(document.name);
      setDescription(document.description);
      setFunctionTypes(document.function_types);
      setPeriods(document.periods);
      setPeriodsNames(document.periods.map(i => i.name));
      setFileName(document.file_name);
    }
  }, [document]);

  const handleFileSelect = e => {
    setFileName(e.target.files[0]?.name || '');
  };

  const handleChangeType = e => setFunctionTypes(e.target.value);

  const handleChangePeriods = e => {
    setPeriods(e.target.value.map(pn => periodChoices.find(pc => pc.name === pn)));
    setPeriodsNames(e.target.value);
  };

  const handleSelectAllFunctions = () => {
    setSelectAllFunctions(prev => !prev);
    setFunctionTypes(prev => !selectAllFunctions ? choices.map(c => c.value) : prev
    );
  };

  const handleSelectAllPeriods = () => {
    setSelectAllPeriods(prev => !prev);
    setPeriods(prev => !selectAllPeriods ? periodChoices : prev);
    setPeriodsNames(prev => !selectAllPeriods ? periodChoices.map(p => p.name) : prev
    );
  };

  const handleSubmit = async () => {
    if (functionTypes.length > 0) {
      const formData = new FormData();
      const isEditing = Boolean(document);
      const selectedFile = refInputFile.current.files[0];
      if (selectedFile) {
        formData.append('file', selectedFile);
        formData.append('file_name', selectedFile.name);
      }
      formData.append('function_types', functionTypes);
      formData.append('season', season);
      formData.append('name', name);
      formData.append('description', description);
      formData.append('periods', JSON.stringify(periods));
      const { error, payload } = await mutate(
        formData,
        isEditing ? document.id : null,
        isEditing ? 'PATCH' : 'POST'
      );
      if (!error) {
        setDocuments(prev => isEditing ? prev.map(doc => (doc.id === document.id ? payload : doc)) : [...prev, payload]
        );
        if (!isEditing) {
          setCount(prev => prev + 1);
        }
        handleClose();
      } else {
        setErrorMessage('Wystąpił błąd');
      }
    }
  };

  return (
    <Portal>
      <Modal open={open} onClose={handleClose}>
        <Card sx={{ ...style, width: '500px' }}>
          <CardHeader
            title={document ? 'Edytuj plik' : 'Dodaj plik'}
            action={
              <IconButton color="primary" onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            }
          />
          <CardContent>
            <Stack
              spacing={3}
              justifyContent="flex-start"
              alignItems="flex-start"
            >
              <SmallFormTextField
                name="name"
                placeholder="Nazwa dokumentu"
                value={name}
                sx={{ width: '100%' }}
                onChange={e => setName(e.target.value)}
              />
              <SmallFormTextField
                name="description"
                placeholder="Opis dokumentu"
                value={description}
                sx={{ width: '100%' }}
                onChange={e => setDescription(e.target.value)}
                multiline
                rows={3}
              />
              <SmallFormTextField
                select
                multiple
                name="functionTypes"
                placeholder="Rodzaj funkcji"
                label="Rodzaj funkcji"
                onChange={handleChangeType}
                value={functionTypes}
                sx={{ width: '100%' }}
                options={choices}
                disabled={selectAllFunctions}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectAllFunctions}
                    onChange={handleSelectAllFunctions}
                  />
                }
                label="Wybierz wszystkie funkcje"
              />
              <Select
                variant="outlined"
                multiple
                placeholder="Wybierz termin"
                labelId="periods"
                value={periodsNames}
                defaultValue={null}
                disabled={selectAllPeriods}
                label="Terminy"
                onChange={e => handleChangePeriods(e)}
                sx={{ width: '100%', height: '60%' }}
              >
                {periodChoices &&
                    periodChoices.map((obj, key) => (
                      <MenuItem key={key} value={obj.name}>
                        {obj.name}
                      </MenuItem>
                    ))}
              </Select>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectAllPeriods}
                    onChange={handleSelectAllPeriods}
                  />
                }
                label="Wybierz wszystkie terminy"
              />
              <Stack direction="row" spacing={2} alignItems="center">
                <Button variant="contained" component="label">
                  Wczytaj plik
                  <input
                    type="file"
                    hidden
                    ref={refInputFile}
                    onChange={handleFileSelect}
                  />
                </Button>
                {fileName && (
                  <Stack direction="row" spacing={1}>
                    <InsertDriveFileOutlinedIcon color="primary" />
                    <Typography variant="subtitle2">{fileName}</Typography>
                  </Stack>
                )}
              </Stack>
              {errorMessage && (
                <Alert
                  variant="outlined"
                  severity="error"
                  sx={{ width: '100%' }}
                >
                  {errorMessage}
                </Alert>
              )}
              <Button
                variant="contained"
                fullWidth
                onClick={handleSubmit}
                disabled={loading}
              >
                Zapisz
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Modal>
    </Portal>
  );
}
